/**
 * ProjectHubPage — Project detail with Plans-based structure
 *
 * Tabs: Planos · Equipe · Arquivos
 * Each plan links to existing modules (/acoes, /problemas, /mapa)
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, Upload,
  UserPlus, Trash2, Users, Paperclip,
  Download, FileText, Plus, ExternalLink,
  ClipboardList, GitBranch, Lightbulb,
  AlertTriangle, Flame, Zap,
  Map as MapIcon, BarChart2, Settings, FolderOpen, Folder,
  FolderPlus, FilePlus2, Image, File, ChevronRight,
  TrendingUp, DollarSign, Target, BookOpen, Shield,
  GanttChart, Link2 as LinkIcon, Activity, Printer,
  CheckCircle2, XCircle, MinusCircle, Bell, Loader2,
} from 'lucide-react';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import {
  EnterpriseModal,
  ModalField,
  modalInputClass,
  modalSelectClass,
  ModalPrimaryButton,
  ModalSecondaryButton,
} from '../components/layout/EnterpriseModal';
import { EnterpriseRibbon } from '../components/layout/EnterpriseRibbon';
import { RibbonDesktopButton } from '../components/ribbon/DesktopRibbonControls';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAppToast } from '../components/AppToast';
import { ProjectService } from '../services/projectService';
import { ProjectPlanService } from '../services/projectPlanService';
import type { ProjectPlan, PlanType, PlanMetrics } from '../types/projectPlan';
import { PLAN_TYPE_LABELS, PLAN_TYPE_COLORS } from '../types/projectPlan';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
  PROJECT_HEALTH_LABELS,
  PROJECT_HEALTH_COLORS,
  MEMBER_ROLE_LABELS,
  OPERATIONAL_ROLE_LABELS,
} from '../types/enterpriseProject';
import type {
  ProjectAttachment,
  ProjectMember,
  ProjectMemberRole,
  OperationalRole,
  ProjectHealth,
} from '../types/enterpriseProject';
import { OrgUserSearch } from '../components/shared/OrgUserSearch';
import type { OrgMember } from '../services/organizationService';
import { InvitationService } from '../services/invitationService';
import type { Invitation } from '../services/invitationService';
import { Mail, Link2, Copy, Check, X } from 'lucide-react';
import { ContextualKPI } from '../components/analytics/ContextualKPI';
import { LinkKpiModal, type CanonicalKpiOption } from '../components/LinkKpiModal';
import { BarChartBlueprints } from '../components/analytics/BarChartBlueprints';
import { useProjectStore } from '../store/useProjectStore';
import ActionListView from '../components/ActionListView';
import { ProjectJourneySidebar, ProjectJourneyHeader } from '../components/ProjectJourneySidebar';
import { WorkspaceModeProvider } from '../context/WorkspaceModeContext';
import type { ProjectStep } from '../components/ProjectJourneySidebar';
import { SustainabilityAuditView } from '../components/consulting/SustainabilityAuditView';
import type { SustainabilityAudit, AuditCriterion } from '../types/consultingProject';
import { DEFAULT_AUDIT_CRITERIA } from '../types/consultingProject';
import { IndicatorService } from '../services/indicatorService';
import { getIndicatorHealth } from '../types/indicator';
import type { Indicator } from '../types/indicator';
import { ProjectRoadmap } from '../components/consulting/ProjectRoadmap';
import { KaizenWeekScheduleView } from '../components/consulting/KaizenWeekScheduleView';
import type { RoadmapItem, KaizenWeekSchedule } from '../types/consultingProject';
import { ProjectRoadmapService, ProjectKaizenScheduleService } from '../services/projectRoadmapService';
import type { KaizenScheduleRecord } from '../services/projectRoadmapService';
import { ActionService } from '../services/actionService';
import { useOrgMembers } from '../hooks/useOrgMembers';

// ─── Types ────────────────────────────────────────────────────
type TabId = 'vsm' | 'actions' | 'kpi' | 'roadmap' | 'management';

interface TabDef { id: TabId; label: string; icon: React.ReactNode }

const TABS: TabDef[] = [
  { id: 'vsm',        label: 'Visão do Processo', icon: <MapIcon size={12} /> },
  { id: 'actions',    label: 'Planos de Ação',    icon: <ClipboardList size={12} /> },
  { id: 'kpi',        label: 'Performance & ROI', icon: <BarChart2 size={12} /> },
  { id: 'roadmap',    label: 'Roadmap',            icon: <GanttChart size={12} /> },
  { id: 'management', label: 'Gestão',             icon: <Settings size={12} /> },
];

// DMS folder types
interface LinkedIndicator {
  indicatorId: string;
  indicatorName: string;
  currentValue: number;
  targetValue?: number | null;
  health: 'healthy' | 'warning' | 'critical' | 'unknown';
}

interface DmsFolder {
  id: string;
  name: string;
  files: DmsFile[];
  isExpanded: boolean;
  subfolders?: DmsFolder[];  // Para lógica de Kaizens
  allowSubfolders?: boolean; // Apenas pasta 04. Kaizens permite subpastas
  linkedIndicator?: LinkedIndicator; // Indicador vinculado à pasta ou kaizen
  responsible?: string; // Responsável pelo kaizen
  needsAudit?: boolean; // Alerta Andon: necessita auditoria de campo
}

interface DmsFile {
  id: string;
  name: string;
  size?: number;
  uploadedAt: string;
  author?: string;
  type: 'pdf' | 'excel' | 'image' | 'word' | 'other';
  url?: string;
}

const DEFAULT_FOLDERS: DmsFolder[] = [
  { id: 'estrategia',      name: '01. Estratégia',                 files: [], isExpanded: false },
  { id: 'gestao-mudanca',  name: '02. Gestão da Mudança',           files: [], isExpanded: false },
  { id: 'mfv',             name: '03. Estruturação de Processos',          files: [], isExpanded: true },
  { id: 'kaizens',         name: '04. Ações de Impacto',                   files: [], isExpanded: false, subfolders: [], allowSubfolders: true },
  { id: 'sustentacao',     name: '05. Monitoramento de Resultados',         files: [], isExpanded: false },
];

const EMPTY_PROJECT_KPIS: Array<{ id: string; name: string; unit: string; type?: Indicator['type']; currentValue: number; targetValue?: number | null; lastReadingDate: string }> = [];

const PLAN_TYPE_OPTIONS: PlanType[] = ['action', 'process', 'improvement'];

const PLAN_ICON: Record<PlanType, React.ReactNode> = {
  action: <ClipboardList size={14} className="text-blue-500" />,
  process: <GitBranch size={14} className="text-emerald-500" />,
  improvement: <Lightbulb size={14} className="text-amber-500" />,
};

// ─── Helpers ──────────────────────────────────────────────────
function fmtDate(v: string | null | undefined) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

function bytesLabel(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Component ────────────────────────────────────────────────
export const ProjectHubPage: React.FC = () => {
  const { id: projectId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const orgId    = useOrganizationId();
  const qc       = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();

  const [activeTab, setActiveTab] = useState<TabId>('vsm');

  // Journey sidebar
  const [showJourney, setShowJourney] = useState(false);
  const [journeyStep, setJourneyStep] = useState(1);

  // DMS folders
  const [dmsKey, setDmsKey] = useState(`dms-${projectId}`);
  const [dmsFolders, setDmsFolders] = useState<DmsFolder[]>(() => {
    try {
      const saved = localStorage.getItem(`dms-${projectId}`);
      return saved ? JSON.parse(saved) : DEFAULT_FOLDERS;
    } catch { return DEFAULT_FOLDERS; }
  });

  const saveDms = useCallback((folders: DmsFolder[]) => {
    setDmsFolders(folders);
    localStorage.setItem(`dms-${projectId}`, JSON.stringify(folders));
  }, [projectId]);

  // Actions from store (filtered by projectId)
  const { getActionsByProject, getCards, actions: allActions } = useProjectStore();
  const journeyDeviationAlert = useProjectStore((s) => s.journeyDeviationAlert);
  const projectActions = useMemo(() => getActionsByProject(projectId), [getActionsByProject, projectId, allActions]);

  // Enterprise actions (with metadata) for team avatar badges
  const { data: enterpriseProjectActions = [] } = useQuery({
    queryKey: ['hub-enterprise-actions', projectId, orgId],
    queryFn: () => ActionService.list(orgId!, { project_id: projectId }),
    staleTime: 60_000,
    enabled: !!orgId && !!projectId,
  });

  // Org members for avatar resolution
  const { members: orgMembersList } = useOrgMembers(orgId);
  const projectCards = useMemo(() => getCards(), [getCards]);
  const actionTab = useState<'all' | 'mine' | 'doing' | 'overdue' | 'done'>('all');
  const activeActionTab = actionTab[0];

  // ROI Calculation: sum gains (indicators) minus sum of action costs
  const totalActionCost = useMemo(
    () => projectActions.reduce((sum, a) => sum + (a.cost ?? 0), 0),
    [projectActions]
  );

  // Auditorias de sustentação (localStorage-backed, scoped by projectId)
  const [audits, setAudits] = useState<SustainabilityAudit[]>(() => {
    try {
      const saved = localStorage.getItem(`audits-${projectId}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const saveAudits = useCallback((next: SustainabilityAudit[]) => {
    setAudits(next);
    localStorage.setItem(`audits-${projectId}`, JSON.stringify(next));
  }, [projectId]);

  const handleAddAudit = useCallback((partial: Partial<SustainabilityAudit>) => {
    const newAudit: SustainabilityAudit = {
      id: `audit-${Date.now()}`,
      projectId,
      kaizenTitle: partial.kaizenTitle ?? '',
      auditDate: partial.auditDate ?? new Date().toISOString(),
      auditor: partial.auditor ?? '',
      area: partial.area ?? '',
      scores: partial.scores ?? [],
      averageScore: partial.averageScore ?? 0,
      status: partial.status ?? 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...partial,
    };
    saveAudits([...audits, newAudit]);
  }, [audits, projectId, saveAudits]);

  const handleUpdateAudit = useCallback((id: string, updates: Partial<SustainabilityAudit>) => {
    saveAudits(audits.map(a => a.id === id ? { ...a, ...updates, updatedAt: new Date().toISOString() } : a));
  }, [audits, saveAudits]);

  // Roadmap (Supabase-backed, real-time for the whole team)
  const { data: roadmapItems = [], refetch: refetchRoadmap } = useQuery({
    queryKey: ['roadmap', orgId, projectId],
    queryFn: () => ProjectRoadmapService.list(orgId, projectId),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const handleAddRoadmapItem = useCallback(async (
    partial: Omit<RoadmapItem, 'id' | 'projectId' | 'createdAt' | 'updatedAt' | 'order'>,
  ) => {
    try {
      await ProjectRoadmapService.create(orgId, {
        ...partial,
        projectId,
        order: roadmapItems.length,
      });
      await refetchRoadmap();
    } catch (err) {
      showToast('error', 'Erro ao criar item do roadmap');
    }
  }, [orgId, projectId, roadmapItems.length, refetchRoadmap, showToast]);

  const handleUpdateRoadmapItem = useCallback(async (id: string, updates: Partial<RoadmapItem>) => {
    try {
      await ProjectRoadmapService.update(orgId, id, updates);
      await refetchRoadmap();
    } catch (err) {
      showToast('error', 'Erro ao atualizar item do roadmap');
    }
  }, [orgId, refetchRoadmap, showToast]);

  const handleDeleteRoadmapItem = useCallback(async (id: string) => {
    try {
      await ProjectRoadmapService.delete(orgId, id);
      await refetchRoadmap();
    } catch (err) {
      showToast('error', 'Erro ao excluir item do roadmap');
    }
  }, [orgId, refetchRoadmap, showToast]);

  // Kaizen schedules: folderId → record (one row per folder, Supabase-backed)
  const [kaizenScheduleCache, setKaizenScheduleCache] = useState<Record<string, KaizenScheduleRecord | null>>({});

  const loadKaizenSchedules = useCallback(async (folderId: string) => {
    if (!orgId || !projectId) return;
    try {
      const record = await ProjectKaizenScheduleService.getByFolder(orgId, projectId, folderId);
      setKaizenScheduleCache(prev => ({ ...prev, [folderId]: record }));
    } catch { /* silent */ }
  }, [orgId, projectId]);

  const handleSaveKaizenSchedule = useCallback(async (
    folderId: string,
    schedules: KaizenWeekSchedule[],
  ) => {
    if (!orgId || !projectId) return;
    try {
      const existing = kaizenScheduleCache[folderId];
      const updated = await ProjectKaizenScheduleService.save(
        orgId, projectId, folderId, schedules, existing?.id ?? null,
      );
      setKaizenScheduleCache(prev => ({ ...prev, [folderId]: updated }));
    } catch {
      showToast('error', 'Erro ao salvar cronograma Kaizen');
    }
  }, [orgId, projectId, kaizenScheduleCache, showToast]);

  // "Ganho Realizado": auditorias com score >= 3 são consideradas ganho validado
  const ganhoRealizadoCount = useMemo(
    () => audits.filter(a => a.averageScore >= 3).length,
    [audits]
  );

  // Indicadores vinculados ao projeto (fetched from Supabase)
  const { data: projectIndicators = [] } = useQuery({
    queryKey: ['indicators', orgId, { project_id: projectId }],
    queryFn: () => IndicatorService.list(orgId, { project_id: projectId }),
    enabled: !!orgId && !!projectId,
    staleTime: 60_000,
  });

  const centralizedProjectKpisRaw = useProjectStore((state) => {
    const current = state.projects.find((project) => project.id === projectId);
    return current?.estado_atual?.kpis;
  });
  const centralizedProjectKpis = centralizedProjectKpisRaw ?? EMPTY_PROJECT_KPIS;

  const unifiedProjectIndicators = useMemo<Indicator[]>(() => {
    const localAsIndicators = centralizedProjectKpis.map((kpi, index) => ({
      id: `local-kpi-${kpi.id || index}`,
      organization_id: orgId,
      name: kpi.name,
      description: null,
      type: (kpi.type ?? 'custom') as Indicator['type'],
      unit: kpi.unit,
      target_value: kpi.targetValue ?? null,
      current_value: kpi.currentValue,
      previous_value: null,
      threshold_warning: null,
      threshold_critical: null,
      direction: 'target_is_best' as const,
      status: 'active' as const,
      frequency: 'daily' as const,
      workflow_id: null,
      action_id: null,
      process_id: null,
      project_id: projectId,
      issue_id: null,
      visibility_scope: 'project' as const,
      owner_id: null,
      created_by: null,
      tags: ['bound_kpi'],
      created_at: kpi.lastReadingDate,
      updated_at: kpi.lastReadingDate,
    }));

    const byName = new Map<string, Indicator>();
    for (const indicator of [...projectIndicators, ...localAsIndicators]) {
      byName.set(indicator.name.trim().toLowerCase(), indicator);
    }
    return Array.from(byName.values());
  }, [centralizedProjectKpis, orgId, projectId, projectIndicators]);

  // Estado de indicadores vinculados a kaizen subpastas (local, por pasta)
  // Armazenado junto ao DMS
  // Andon: any linked indicator in critical state
  const andonCritical = useMemo<boolean>(() => {
    if (unifiedProjectIndicators.some(ind => getIndicatorHealth(ind) === 'critical')) return true;
    // Check kaizen subfolder linked indicators via dmsFolders
    const kaizens = dmsFolders.find(f => f.id === 'kaizens');
    const subs = kaizens?.subfolders ?? [];
    return subs.some(s => s.linkedIndicator?.health === 'critical');
  }, [dmsFolders, unifiedProjectIndicators]);

  // Mark sustentação folder with needsAudit when andon is critical
  const andonSustentacaoAlert = useMemo<boolean>(() => andonCritical, [andonCritical]);

  // Modal para vincular indicador a kaizen subfolder
  const [linkIndicatorModal, setLinkIndicatorModal] = useState<{ folderId: string; subId: string } | null>(null);
  const [linkIndicatorSearch, setLinkIndicatorSearch] = useState('');
  const [showLinkProjectKpiModal, setShowLinkProjectKpiModal] = useState(false);
  const [bindingProjectKpi, setBindingProjectKpi] = useState(false);

  // All org indicators (for linking picker)
  const { data: allOrgIndicators = [] } = useQuery({
    queryKey: ['indicators', orgId, 'all'],
    queryFn: () => IndicatorService.list(orgId),
    enabled: !!orgId && !!linkIndicatorModal,
    staleTime: 60_000,
  });

  const handleLinkIndicator = useCallback((indicator: Indicator) => {
    if (!linkIndicatorModal) return;
    const { folderId, subId } = linkIndicatorModal;
    const health = getIndicatorHealth(indicator);
    const linked: LinkedIndicator = {
      indicatorId: indicator.id,
      indicatorName: indicator.name,
      currentValue: indicator.current_value,
      targetValue: indicator.target_value,
      health,
    };
    const updated = dmsFolders.map(f => {
      if (f.id !== folderId) return f;
      return {
        ...f,
        subfolders: (f.subfolders ?? []).map(s =>
          s.id === subId ? { ...s, linkedIndicator: linked } : s
        ),
      };
    });
    saveDms(updated);
    setLinkIndicatorModal(null);
    setLinkIndicatorSearch('');
  }, [linkIndicatorModal, dmsFolders, saveDms]);

  const handleBindProjectKpi = useCallback(async (kpi: CanonicalKpiOption, targetValue: number | null) => {
    if (!orgId || !projectId) return;
    const nowIso = new Date().toISOString();
    setBindingProjectKpi(true);

    try {
      useProjectStore.setState((state) => ({
        projects: state.projects.map((project) => {
          if (project.id !== projectId) return project;
          const existing = project.estado_atual?.kpis ?? [];
          const nextKpis = [
            ...existing.filter((item) => item.name.trim().toLowerCase() !== kpi.name.trim().toLowerCase()),
            {
              id: kpi.id,
              name: kpi.name,
              unit: kpi.unit,
              type: kpi.type,
              currentValue: kpi.initialValue,
              targetValue,
              lastReadingDate: nowIso,
            },
          ];
          return {
            ...project,
            estado_atual: {
              ...project.estado_atual,
              kpis: nextKpis,
            },
          };
        }),
      }));

      const existingIndicators = await IndicatorService.list(orgId, { project_id: projectId, search: kpi.name });
      const exact = existingIndicators.find((item) => item.name.trim().toLowerCase() === kpi.name.trim().toLowerCase());

      if (!exact) {
        const created = await IndicatorService.create(orgId, {
          name: kpi.name,
          type: kpi.type,
          unit: kpi.unit,
          direction: kpi.direction,
          status: 'active',
          frequency: 'daily',
          current_value: kpi.initialValue,
          target_value: targetValue,
          project_id: projectId,
          visibility_scope: 'project',
          tags: ['stp_canonico', 'journey_step5'],
        });
        await IndicatorService.recordSnapshot(orgId, created.id, kpi.initialValue);
      } else if (targetValue != null && exact.target_value !== targetValue) {
        await IndicatorService.update(orgId, exact.id, { target_value: targetValue });
      }

      await qc.invalidateQueries({ queryKey: ['indicators'] });
      setShowLinkProjectKpiModal(false);
      showToast('success', 'KPI vinculado com sucesso à Gestão à Vista!');
    } catch {
      showToast('error', 'Falha ao vincular KPI neste projeto.');
    } finally {
      setBindingProjectKpi(false);
    }
  }, [orgId, projectId, qc, showToast]);

  // Modal de relatório One-Page Kaizen
  const [kaizenReportModal, setKaizenReportModal] = useState<{ sub: DmsFolder; parentName: string } | null>(null);

  // A3 Project Report
  const [showA3Report, setShowA3Report] = useState(false);
  const [a3Generating, setA3Generating] = useState(false);

  const handleExportA3 = useCallback(() => {
    setA3Generating(true);
    setShowA3Report(true);
    // Give browser a tick to render the print view, then print
    setTimeout(() => {
      setA3Generating(false);
    }, 800);
  }, []);

  // Performance tab: Pareto filter + ganho pulse + audit modal
  const [paretoFilter, setParetoFilter] = useState<string | null>(null);
  const [ganhoJustPulsed, setGanhoJustPulsed] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  useEffect(() => {
    const state = (location.state ?? {}) as { step5Focus?: 'kpi' | 'audit'; openAuditModal?: boolean };
    if (state.step5Focus === 'kpi') {
      setActiveTab('kpi');
    }
    if (state.openAuditModal) {
      setActiveTab('kpi');
      setShowAuditModal(true);
    }
  }, [location.state]);

  const prevGanhoRef = React.useRef(ganhoRealizadoCount);
  React.useEffect(() => {
    if (ganhoRealizadoCount > prevGanhoRef.current) {
      setGanhoJustPulsed(true);
      const t = setTimeout(() => setGanhoJustPulsed(false), 2000);
      prevGanhoRef.current = ganhoRealizadoCount;
      return () => clearTimeout(t);
    }
    prevGanhoRef.current = ganhoRealizadoCount;
  }, [ganhoRealizadoCount]);

  // Plan modal
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan]     = useState<ProjectPlan | null>(null);
  const [planName, setPlanName]           = useState('');
  const [planType, setPlanType]           = useState<PlanType>('action');
  const [planDesc, setPlanDesc]           = useState('');
  const [savingPlan, setSavingPlan]       = useState(false);

  // Team modal
  const [showAddMember, setShowAddMember]    = useState(false);
  const [selectedMember, setSelectedMember]  = useState<OrgMember | null>(null);
  const [addMemberRole, setAddMemberRole]    = useState<ProjectMemberRole>('viewer');
  const [addOperationalRole, setAddOperationalRole] = useState<OperationalRole>('executor');
  const [addingMember, setAddingMember]      = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail]         = useState('');
  const [inviteRole, setInviteRole]           = useState<ProjectMemberRole>('viewer');
  const [inviteOpRole, setInviteOpRole]       = useState<OperationalRole>('executor');
  const [sendingInvite, setSendingInvite]     = useState(false);
  const [inviteLink, setInviteLink]           = useState<string | null>(null);
  const [linkCopied, setLinkCopied]           = useState(false);

  // Files
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // ── Core project
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', orgId, projectId],
    queryFn: () => ProjectService.getById(orgId, projectId),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  // ── Plans
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['project-plans', orgId, projectId],
    queryFn: () => ProjectPlanService.listPlans(orgId, projectId),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: planMetrics = [] } = useQuery({
    queryKey: ['project-plan-metrics', orgId, projectId],
    queryFn: () => ProjectPlanService.listPlanMetrics(orgId, projectId),
    enabled: !!orgId && !!projectId,
    staleTime: 60_000,
  });

  const metricsById = useMemo(() => {
    const m: Record<string, PlanMetrics> = {};
    planMetrics.forEach(x => { m[x.plan_id] = x; });
    return m;
  }, [planMetrics]);

  // ── Aggregated KPIs
  const summary = useMemo(() => {
    return ProjectPlanService.buildSummary(planMetrics);
  }, [planMetrics]);

  // ── Has any real data?
  const hasPlanData = useMemo(() => {
    return summary.actions_total > 0 || summary.issues_total > 0 || summary.executions_total > 0;
  }, [summary]);

  // ── Health
  const health = useMemo<ProjectHealth>(() => {
    if (!project) return 'healthy';
    const isOverdue = !!project.target_date && new Date(project.target_date) < new Date() && project.status !== 'completed' && project.status !== 'archived';
    const metricHealth = ProjectPlanService.computeProjectHealth(summary);
    if (isOverdue && metricHealth !== 'healthy') return 'critical';
    if (isOverdue || metricHealth !== 'healthy') return metricHealth === 'critical' ? 'critical' : 'warning';
    return metricHealth;
  }, [project, summary]);

  // ── Team (lazy)
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['project-members', orgId, projectId],
    queryFn: () => ProjectService.listMembers(projectId),
    enabled: !!orgId && !!projectId && activeTab === 'management',
    staleTime: 60_000,
  });

  // ── Files (lazy)
  const { data: attachments = [], isLoading: attachmentsLoading } = useQuery({
    queryKey: ['project-attachments', projectId],
    queryFn: () => ProjectService.listAttachments(projectId),
    enabled: !!projectId && activeTab === 'management',
    staleTime: 30_000,
  });

  // ── Handlers
  const onRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['project', orgId, projectId] });
    qc.invalidateQueries({ queryKey: ['project-plans', orgId, projectId] });
    qc.invalidateQueries({ queryKey: ['project-plan-metrics', orgId, projectId] });
    qc.invalidateQueries({ queryKey: ['project-members', orgId, projectId] });
    qc.invalidateQueries({ queryKey: ['project-attachments', projectId] });
  }, [qc, orgId, projectId]);

  const goBack = useCallback(() => navigate('/projetos'), [navigate]);

  // Plan CRUD
  const resetPlanForm = useCallback(() => {
    setEditingPlan(null); setPlanName(''); setPlanType('action'); setPlanDesc('');
  }, []);

  const openCreatePlan = useCallback(() => { resetPlanForm(); setShowPlanModal(true); }, [resetPlanForm]);

  const openEditPlan = useCallback((p: ProjectPlan) => {
    setEditingPlan(p); setPlanName(p.name); setPlanType(p.type); setPlanDesc(p.description ?? '');
    setShowPlanModal(true);
  }, []);

  const handleSavePlan = useCallback(async () => {
    if (!planName.trim()) { showToast('warning', 'Nome é obrigatório'); return; }
    setSavingPlan(true);
    try {
      if (editingPlan) {
        await ProjectPlanService.updatePlan(orgId, editingPlan.id, { name: planName.trim(), description: planDesc.trim() || undefined });
        showToast('success', 'Plano atualizado');
      } else {
        await ProjectPlanService.createPlan(orgId, { project_id: projectId, name: planName.trim(), type: planType, description: planDesc.trim() || undefined });
        showToast('success', 'Plano criado');
      }
      setShowPlanModal(false); resetPlanForm();
      qc.invalidateQueries({ queryKey: ['project-plans', orgId, projectId] });
      qc.invalidateQueries({ queryKey: ['project-plan-metrics', orgId, projectId] });
      qc.invalidateQueries({ queryKey: ['project-plan-summaries'] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro ao salvar');
    } finally { setSavingPlan(false); }
  }, [editingPlan, orgId, projectId, planName, planType, planDesc, resetPlanForm, qc, showToast]);

  const handleDeletePlan = useCallback(async (p: ProjectPlan) => {
    if (!window.confirm(`Excluir plano "${p.name}"?`)) return;
    try {
      await ProjectPlanService.deletePlan(orgId, p.id);
      showToast('success', 'Plano excluído');
      qc.invalidateQueries({ queryKey: ['project-plans', orgId, projectId] });
      qc.invalidateQueries({ queryKey: ['project-plan-summaries'] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro');
    }
  }, [orgId, projectId, qc, showToast]);

  // Navigate to module filtered by plan
  const openPlanModule = useCallback((plan: ProjectPlan) => {
    switch (plan.type) {
      case 'action':      navigate(`/acoes?planId=${plan.id}`); break;
      case 'process':     navigate(`/mapa?planId=${plan.id}`); break;
      case 'improvement': navigate(`/problemas?planId=${plan.id}`); break;
    }
  }, [navigate]);

  // Team
  const existingMemberIds = useMemo(() => members.map(m => m.user_id), [members]);

  const handleAddMember = useCallback(async () => {
    if (!selectedMember) { showToast('warning', 'Selecione um membro'); return; }
    setAddingMember(true);
    try {
      await ProjectService.addMember(projectId, selectedMember.user_id, addMemberRole, addOperationalRole);
      showToast('success', 'Membro adicionado');
      setShowAddMember(false); setSelectedMember(null);
      qc.invalidateQueries({ queryKey: ['project-members', orgId, projectId] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro ao adicionar membro');
    } finally { setAddingMember(false); }
  }, [selectedMember, addMemberRole, addOperationalRole, projectId, orgId, qc, showToast]);

  const handleRemoveMember = useCallback(async (m: ProjectMember) => {
    if (!window.confirm('Remover membro do projeto?')) return;
    try {
      await ProjectService.removeMember(m.id);
      showToast('success', 'Membro removido');
      qc.invalidateQueries({ queryKey: ['project-members', orgId, projectId] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro');
    }
  }, [orgId, projectId, qc, showToast]);

  // ── Invitations
  const { data: pendingInvites = [] } = useQuery({
    queryKey: ['project-invitations', orgId, projectId],
    queryFn: () => InvitationService.list(orgId, { status: 'pending', projectId }),
    enabled: !!orgId && !!projectId && activeTab === 'management',
    staleTime: 30_000,
  });

  const handleSendInvite = useCallback(async () => {
    if (!inviteEmail.trim()) { showToast('warning', 'Informe o email'); return; }
    setSendingInvite(true);
    try {
      const inv = await InvitationService.create({
        organization_id: orgId,
        email: inviteEmail.trim(),
        project_id: projectId,
        project_role: inviteRole,
        operational_role: inviteOpRole,
      });
      const link = InvitationService.buildInviteLink(inv.token);
      setInviteLink(link);
      showToast('success', 'Convite criado');
      qc.invalidateQueries({ queryKey: ['project-invitations', orgId, projectId] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro ao criar convite');
    } finally { setSendingInvite(false); }
  }, [inviteEmail, inviteRole, inviteOpRole, orgId, projectId, qc, showToast]);

  const handleCopyLink = useCallback(() => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, [inviteLink]);

  const handleRevokeInvite = useCallback(async (inv: Invitation) => {
    if (!window.confirm(`Revogar convite para ${inv.email}?`)) return;
    try {
      await InvitationService.revoke(inv.id);
      showToast('success', 'Convite revogado');
      qc.invalidateQueries({ queryKey: ['project-invitations', orgId, projectId] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro');
    }
  }, [orgId, projectId, qc, showToast]);

  const resetInviteModal = useCallback(() => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteLink(null);
    setLinkCopied(false);
  }, []);

  // Files
  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const userId = await ProjectService.getCurrentUserId();
      await ProjectService.uploadAttachment(projectId, orgId, userId, file);
      showToast('success', 'Arquivo enviado');
      qc.invalidateQueries({ queryKey: ['project-attachments', projectId] });
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Erro no upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [projectId, orgId, qc, showToast]);

  const handleDeleteAttachment = useCallback(async (a: ProjectAttachment) => {
    if (!window.confirm(`Excluir arquivo "${a.name}"?`)) return;
    try {
      await ProjectService.deleteAttachment(a.id, a.file_path);
      showToast('success', 'Arquivo excluído');
      qc.invalidateQueries({ queryKey: ['project-attachments', projectId] });
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro');
    }
  }, [projectId, qc, showToast]);

  // ── Ribbon
  const ribbon = useMemo(() => (
    <EnterpriseRibbon moduleId="projetos-hub">
      <EnterpriseRibbon.Group>
        <RibbonDesktopButton icon={<ArrowLeft strokeWidth={1.5} />}  label="Voltar"    onClick={goBack} />
        <RibbonDesktopButton icon={<RefreshCw strokeWidth={1.5} />}  label="Atualizar" onClick={onRefresh} />
        <RibbonDesktopButton icon={<GanttChart strokeWidth={1.5} />} label="Jornada"   onClick={() => setShowJourney(v => !v)} />
      </EnterpriseRibbon.Group>

      {activeTab === 'vsm' && (
        <EnterpriseRibbon.Group>
          <RibbonDesktopButton icon={<MapIcon strokeWidth={1.5} />} label="Abrir Mapa" onClick={() => navigate(`/mapa?projectId=${projectId}`)} />
        </EnterpriseRibbon.Group>
      )}

      {activeTab === 'actions' && (
        <EnterpriseRibbon.Group>
          <RibbonDesktopButton icon={<ExternalLink strokeWidth={1.5} />} label="Abrir Central de Ações" onClick={() => navigate(`/acoes?projectId=${projectId}`)} />
        </EnterpriseRibbon.Group>
      )}

      {activeTab === 'management' && (
        <EnterpriseRibbon.Group>
          <RibbonDesktopButton icon={<UserPlus strokeWidth={1.5} />} label="Adicionar Membro" onClick={() => setShowAddMember(true)} />
          <RibbonDesktopButton icon={<Mail strokeWidth={1.5} />} label="Convidar por Email" onClick={() => setShowInviteModal(true)} />
          <RibbonDesktopButton icon={<Upload strokeWidth={1.5} />} label="Anexar Arquivo" onClick={() => fileRef.current?.click()} disabled={uploading} />
        </EnterpriseRibbon.Group>
      )}
      <EnterpriseRibbon.Group>
        <button
          onClick={handleExportA3}
          disabled={a3Generating}
          className="flex flex-col items-center gap-0.5 rounded px-2 py-1 text-slate-500 hover:bg-blue-600/10 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 disabled:opacity-50 transition-colors"
          title="Gerar Relatório de Projeto"
        >
          {a3Generating
            ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
            : <Printer size={16} strokeWidth={1.5} />
          }
          <span className="text-[10px] font-medium leading-none">{a3Generating ? 'Gerando...' : 'Relatório'}</span>
        </button>
      </EnterpriseRibbon.Group>
    </EnterpriseRibbon>
  ), [goBack, onRefresh, activeTab, uploading, navigate, projectId, a3Generating, handleExportA3]);

  // ── Members columns
  const membersColumns = useMemo<DataGridColumn<ProjectMember>[]>(() => [
    { key: 'user_id', label: 'Membro', width: '35%', render: r => (
      <div className="min-w-0">
        <p className="text-[12px] font-medium text-slate-800 dark:text-slate-200 truncate">{r.user_name ?? r.user_id}</p>
        {r.user_email && <p className="text-[10px] text-slate-400 truncate">{r.user_email}</p>}
      </div>
    )},
    { key: 'role',    label: 'Permissão',   width: '18%', render: r => (
      <EnterpriseBadge className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
        {MEMBER_ROLE_LABELS[r.role]}
      </EnterpriseBadge>
    )},
    { key: 'operational_role', label: 'Função', width: '18%', render: r => (
      r.operational_role ? (
        <EnterpriseBadge className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          {OPERATIONAL_ROLE_LABELS[r.operational_role]}
        </EnterpriseBadge>
      ) : <span className="text-[11px] text-slate-400">—</span>
    )},
    { key: 'created_at', label: 'Desde', width: '15%', mono: true, render: r => fmtDate(r.created_at) },
    { key: 'actions',    label: '',      width: '10%', render: r => (
      <button
        onClick={e => { e.stopPropagation(); handleRemoveMember(r); }}
        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
        title="Remover membro"
      >
        <Trash2 size={12} />
      </button>
    )},
  ], [handleRemoveMember]);

  // ─── Render ──────────────────────────────────────────────────
  if (projectLoading) {
    return (
      <ModuleLayout ribbon={ribbon}>
        <div className="flex h-full items-center justify-center text-[12px] text-slate-400">Carregando projeto...</div>
      </ModuleLayout>
    );
  }

  if (!project) {
    return (
      <ModuleLayout ribbon={ribbon}>
        <div className="flex h-full flex-col items-center justify-center gap-3 text-[12px] text-slate-400">
          <p>Projeto não encontrado.</p>
          <button onClick={goBack} className="text-blue-500 hover:underline text-[11px]">← Voltar a Projetos</button>
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout ribbon={ribbon}>
      <div className="flex h-full flex-col overflow-hidden">

        {/* ── HEADER ── */}
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              {/* Breadcrumb */}
              <div className="mb-0.5 flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                <span>Workflow Manager</span>
                <span className="text-slate-300 dark:text-slate-600">/</span>
                <button onClick={goBack} className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">Projetos</button>
                <span className="text-slate-300 dark:text-slate-600">/</span>
                <span className="font-medium text-slate-700 dark:text-slate-300 truncate max-w-[200px]">{project.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 truncate">
                  {project.name}
                </h1>
                <EnterpriseBadge className={PROJECT_STATUS_COLORS[project.status]}>
                  {PROJECT_STATUS_LABELS[project.status]}
                </EnterpriseBadge>
                <EnterpriseBadge className={PROJECT_HEALTH_COLORS[health]}>
                  {PROJECT_HEALTH_LABELS[health]}
                </EnterpriseBadge>
                {/* Alert badges */}
                {summary.actions_overdue > 0 && (
                  <span className="flex items-center gap-1 rounded-sm bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    <AlertTriangle size={9} />{summary.actions_overdue} atrasadas
                  </span>
                )}
                {summary.issues_critical > 0 && (
                  <span className="flex items-center gap-1 rounded-sm bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                    <Flame size={9} />{summary.issues_critical} críticos
                  </span>
                )}
                {andonCritical && (
                  <span className="flex animate-pulse items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
                    <Bell size={9} />ANDON
                  </span>
                )}
              </div>
              {project.description && (
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1">
                  {project.description}
                </p>
              )}

              {/* Compact Journey Header (collapsible) */}
              <div className="mt-3">
                <ProjectJourneyHeader
                  currentIndex={journeyStep}
                  total={5}
                  showDeviationAlert={journeyDeviationAlert}
                  isExpanded={showJourney}
                  compact={true}
                  onToggle={() => setShowJourney(s => !s)}
                />
              </div>
            </div>
          </div>

          {/* KPI STRIP */}
          <div className="mt-2 flex flex-wrap items-center gap-4 text-[10px] text-slate-500 dark:text-slate-400">
            {hasPlanData && <KpiChip label="Progresso" value={`${summary.progress}%`} />}
            {summary.executions_active > 0 && <KpiChip label="Execuções ativas" value={summary.executions_active} />}
            {projectActions.length > 0 && <KpiChip label="Ações" value={projectActions.length} />}
            {totalActionCost > 0 && (
              <KpiChip label="Custo Ações" value={`R$ ${totalActionCost.toLocaleString('pt-BR')}`} warn />
            )}
            {project.target_date && (
              <span>
                Meta: <strong className={
                  !!project.target_date && new Date(project.target_date) < new Date() && project.status !== 'completed'
                    ? 'text-red-500' : 'text-slate-700 dark:text-slate-300'
                }>{fmtDate(project.target_date)}</strong>
              </span>
            )}

          </div>
        </div>

        {/* ── BODY: tabs + optional sidebar ── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">

          {/* Main content area */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">

            {/* ── TAB BAR ── */}
            <div className="shrink-0 flex items-center gap-0 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`relative flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-[11px] font-medium transition-all ${
                    activeTab === t.id
                      ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-300'
                  }`}
                >
                  {t.icon}
                  {t.label}
                  {/* Alert dot on actions tab if overdue */}
                  {t.id === 'actions' && summary.actions_overdue > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500" />
                  )}
                  {/* Alert dot on kpi tab if critical */}
                  {t.id === 'kpi' && summary.issues_critical > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-orange-500" />
                  )}
                </button>
              ))}
            </div>

            {/* ── TAB CONTENT with fade transition ── */}
            <div className="min-h-0 flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">

              {/* ── VSM TAB ── */}
              {activeTab === 'vsm' && (
                <div className="flex h-full flex-col items-center justify-center p-6 animate-in fade-in duration-200">
                  <div className="w-full max-w-2xl rounded border border-slate-200/60 bg-white/80 backdrop-blur-xl p-8 shadow-lg dark:border-slate-700/60 dark:bg-slate-900/40">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="rounded bg-blue-100 p-2.5 dark:bg-blue-900/30">
                        <MapIcon size={20} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h2 className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Mapa VSM</h2>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Value Stream Map filtrado por este projeto</p>
                      </div>
                    </div>

                    {/* VSM Summary Stats */}
                    <div className="mb-6 grid grid-cols-3 gap-3">
                      <div className="rounded border border-slate-100 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/50">
                        <p className="text-[18px] font-bold text-slate-800 dark:text-slate-100">{projectCards.length}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Processos</p>
                      </div>
                      <div className="rounded border border-slate-100 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/50">
                        <p className="text-[18px] font-bold text-amber-600 dark:text-amber-400">{summary.actions_overdue}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Kaizens atrasados</p>
                      </div>
                      <div className="rounded border border-slate-100 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/50">
                        <p className="text-[18px] font-bold text-emerald-600 dark:text-emerald-400">{projectActions.filter(a => (a.cardData?.status ?? a.status) === 'done').length}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Ações concluídas</p>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/mapa?projectId=${projectId}`)}
                      className="flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-4 py-3 text-[12px] font-semibold text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
                    >
                      <MapIcon size={14} />
                      Abrir Canvas VSM
                    </button>
                    <p className="mt-2 text-center text-[10px] text-slate-400">
                      Ações criadas no VSM herdam automaticamente este projeto
                    </p>
                  </div>
                </div>
              )}

              {/* ── ACTIONS TAB ── */}
              {activeTab === 'actions' && (
                <div className="flex h-full flex-col animate-in fade-in duration-200">
                  {projectActions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-[12px] text-slate-400">
                      <ClipboardList size={28} className="mb-2 text-slate-300 dark:text-slate-600" />
                      <p className="font-medium text-slate-600 dark:text-slate-300">Nenhuma ação vinculada</p>
                      <p className="mt-1 text-[11px]">Crie ações na Central ou no VSM — elas aparecerão aqui</p>
                      <button
                        onClick={() => navigate(`/acoes?projectId=${projectId}`)}
                        className="mt-4 rounded bg-blue-50 px-4 py-2 text-[11px] font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        Abrir Central de Ações →
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-1 min-h-0 flex-col">
                      {/* Team avatar strip — mostra por ação quando há members vinculados */}
                      {enterpriseProjectActions.some(a => (a.metadata?.team_members ?? []).length > 0) && (
                        <div className="shrink-0 border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">Equipes vinculadas</p>
                          <div className="flex flex-col gap-1">
                            {enterpriseProjectActions
                              .filter(a => (a.metadata?.team_members ?? []).length > 0)
                              .map(action => {
                                const teamIds: string[] = action.metadata?.team_members ?? [];
                                const AVATAR_COLORS = [
                                  'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                                  'bg-emerald-200 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
                                  'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
                                  'bg-violet-200 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
                                  'bg-rose-200 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
                                ];
                                return (
                                  <div key={action.id} className="flex items-center gap-2">
                                    <span className="w-[160px] shrink-0 truncate text-[11px] text-[var(--text-secondary)]" title={action.title}>
                                      {action.title}
                                    </span>
                                    <div className="flex items-center gap-0.5">
                                      {teamIds.slice(0, 5).map((memberId, i) => {
                                        const member = orgMembersList.find(m => m.id === memberId);
                                        const initials = member
                                          ? member.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
                                          : '?';
                                        return (
                                          <span
                                            key={memberId}
                                            title={member?.name ?? memberId}
                                            className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm text-[8px] font-bold ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                                          >
                                            {initials}
                                          </span>
                                        );
                                      })}
                                      {teamIds.length > 5 && (
                                        <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm bg-slate-200 text-[8px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                          +{teamIds.length - 5}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                      <ActionListView
                        actions={projectActions}
                        cards={projectCards}
                        onActionClick={() => {}}
                        onUpdateAction={() => {}}
                        activeTab={activeActionTab}
                        groupBy="none"
                        onNavigateToCard={(cardId) => navigate(`/mapa?cardId=${cardId}`)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ── KPI + ROI TAB — Zero Scroll ── */}
              {activeTab === 'kpi' && (
                <div className="animate-in fade-in duration-200 h-full flex flex-col gap-2 p-2.5 overflow-hidden">

                  {/* ─ ROW 1: ScoreboardStrip ─ */}
                  <div className="shrink-0 grid grid-cols-4 gap-2">
                    {/* Custo Total */}
                    <div className="rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl px-3 py-2 dark:bg-slate-900/60">
                      <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1"><DollarSign size={10} className="text-red-400" />Custo Total</p>
                      <p className="text-lg font-bold text-red-400 leading-tight">
                        R$ {totalActionCost.toLocaleString('pt-BR')}
                      </p>
                      <p className="text-[9px] text-slate-500">{projectActions.length} ações (5W2H)</p>
                    </div>
                    {/* Ganho Realizado */}
                    <div className={`rounded border border-white/10 backdrop-blur-xl px-3 py-2 transition-all duration-300 ${
                      ganhoJustPulsed
                        ? 'bg-emerald-500/30 border-emerald-400/60 shadow-lg shadow-emerald-500/20'
                        : 'bg-slate-900/40 dark:bg-slate-900/60'
                    }`}>
                      <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1"><TrendingUp size={10} className="text-emerald-400" />Ganho Realizado</p>
                      <p className={`text-lg font-bold leading-tight transition-colors duration-300 ${ganhoJustPulsed ? 'text-emerald-300' : 'text-emerald-400'}`}>
                        {ganhoRealizadoCount} kaizen{ganhoRealizadoCount !== 1 ? 's' : ''}
                      </p>
                      <p className="text-[9px] text-slate-500">auditorias score ≥ 3</p>
                    </div>
                    {/* ROI Líquido */}
                    <div className="rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl px-3 py-2 dark:bg-slate-900/60">
                      <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1"><Target size={10} className="text-blue-400" />ROI Líquido</p>
                      <p className={`text-lg font-bold leading-tight ${totalActionCost === 0 ? 'text-slate-400' : ganhoRealizadoCount * 1 - totalActionCost / 10000 >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                        {ganhoRealizadoCount > 0 || totalActionCost > 0
                          ? `${ganhoRealizadoCount} validados`
                          : '—'}
                      </p>
                      <p className="text-[9px] text-slate-500">kaizens - custo operacional</p>
                    </div>
                    {/* Evolução */}
                    <div className="rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl px-3 py-2 dark:bg-slate-900/60">
                      <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1"><BarChart2 size={10} className="text-violet-400" />% Evolução</p>
                      <p className="text-lg font-bold text-violet-400 leading-tight">{summary.progress}%</p>
                      <p className="text-[9px] text-slate-500">{summary.actions_completed ?? 0} / {summary.actions_total ?? 0} ações</p>
                    </div>
                  </div>

                  {/* ─ ROW 2: ContextualKPI (7) + Pareto (5) ─ */}
                  <div className="min-h-0 flex-1 grid grid-cols-12 gap-2">

                    {/* Left — Indicators panel (7 cols) */}
                    <div className="col-span-7 flex flex-col gap-2 min-h-0 overflow-hidden">
                      {/* Semaphore linked indicators (compact) */}
                      {(() => {
                        const kaizens = dmsFolders.find(f => f.id === 'kaizens');
                        const linkedSubs = (kaizens?.subfolders ?? []).filter(s => s.linkedIndicator);
                        const allItems = [
                          ...linkedSubs.map(s => ({ id: s.id, name: s.name, sub: s.linkedIndicator!.indicatorName, val: s.linkedIndicator!.currentValue, target: s.linkedIndicator?.targetValue, h: s.linkedIndicator!.health })),
                          ...unifiedProjectIndicators.map(i => ({ id: i.id, name: i.name, sub: 'Projeto', val: i.current_value, target: i.target_value, h: getIndicatorHealth(i) })),
                        ];
                        if (allItems.length === 0) return null;
                        return (
                          <div className="shrink-0 flex items-center gap-1.5 flex-wrap">
                            {allItems.map(item => {
                              const isActive = paretoFilter === item.id;
                              const ring = item.h === 'healthy' ? 'border-emerald-500/50 text-emerald-400' : item.h === 'warning' ? 'border-amber-500/50 text-amber-400' : item.h === 'critical' ? 'border-red-500/50 text-red-400' : 'border-slate-600 text-slate-400';
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => setParetoFilter(isActive ? null : item.id)}
                                  className={`flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[10px] font-medium transition-all ${ring} ${isActive ? 'bg-white/10 shadow-sm' : 'bg-transparent opacity-70 hover:opacity-100'}`}
                                  title={`${item.name}: ${item.val}${item.target != null ? ` / meta ${item.target}` : ''}`}
                                >
                                  {item.h === 'healthy' ? <CheckCircle2 size={9} /> : item.h === 'warning' ? <MinusCircle size={9} /> : item.h === 'critical' ? <XCircle size={9} /> : <Activity size={9} />}
                                  <span className="max-w-[80px] truncate">{item.name}</span>
                                  <span className="font-mono font-bold">{item.val}</span>
                                </button>
                              );
                            })}
                            {paretoFilter && (
                              <button onClick={() => setParetoFilter(null)} className="text-[9px] text-slate-500 hover:text-slate-300 ml-1">× limpar</button>
                            )}
                          </div>
                        );
                      })()}

                      {/* ContextualKPI */}
                      <div className="min-h-0 flex-1 rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl overflow-hidden dark:bg-slate-900/60">
                        <div className="flex items-center gap-1.5 border-b border-white/5 px-3 py-1.5">
                          <Target size={11} className="text-blue-400" />
                          <span className="text-xs font-semibold text-slate-300">Indicadores do Projeto</span>
                          {andonCritical && <span className="ml-auto flex animate-pulse items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white"><Bell size={7} />ANDON</span>}
                        </div>
                        <div className="p-2 h-[calc(100%-28px)] overflow-auto">
                          <ContextualKPI projectId={projectId} organizationId={orgId} layout="grid" onRequestBindKpi={() => setShowLinkProjectKpiModal(true)} />
                        </div>
                      </div>
                    </div>

                    {/* Right — Pareto compacto (5 cols) */}
                    <div className="col-span-5 min-h-0 flex flex-col rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl overflow-hidden dark:bg-slate-900/60">
                      <div className="shrink-0 flex items-center gap-1.5 border-b border-white/5 px-3 py-1.5">
                        <BarChart2 size={11} className="text-purple-400" />
                        <span className="text-xs font-semibold text-slate-300">Principais Perdas</span>
                        {paretoFilter && (
                          <span className="ml-auto rounded bg-violet-600/30 px-1.5 py-0.5 text-[9px] text-violet-300 border border-violet-500/30">
                            filtrado
                          </span>
                        )}
                      </div>
                      <div className="min-h-0 flex-1 overflow-hidden p-2">
                        <BarChartBlueprints />
                      </div>
                    </div>
                  </div>

                  {/* ─ ROW 3: Audit History Strip ─ */}
                  <div className="shrink-0 rounded border border-white/10 bg-slate-900/40 backdrop-blur-xl dark:bg-slate-900/60 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5">
                      <Shield size={11} className="text-emerald-400" />
                      <span className="text-xs font-semibold text-slate-300">Histórico de Auditorias</span>
                      <span className="text-[9px] text-slate-500">{audits.length} registro{audits.length !== 1 ? 's' : ''}</span>
                      <button
                        onClick={() => setShowAuditModal(true)}
                        className="ml-auto flex items-center gap-1 rounded bg-emerald-600/20 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-medium text-emerald-400 hover:bg-emerald-600/30 transition-colors"
                      >
                        <Plus size={10} />Nova Auditoria
                      </button>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 overflow-x-auto">
                      {audits.length === 0 ? (
                        <p className="text-[10px] text-slate-500 italic">Nenhuma auditoria registrada — clique em Nova Auditoria para começar</p>
                      ) : (
                        [...audits].reverse().slice(0, 10).map(a => {
                          const score = a.averageScore ?? 0;
                          const color = score >= 4 ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : score >= 3 ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : score >= 2 ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-red-500/20 border-red-500/40 text-red-300';
                          return (
                            <div key={a.id} className={`shrink-0 flex flex-col items-center rounded border px-2 py-1 ${color}`} title={`${a.kaizenTitle} — ${a.auditor}`}>
                              <span className="text-sm font-bold leading-none">{score.toFixed(1)}</span>
                              <span className="text-[8px] mt-0.5 max-w-[60px] truncate">{a.kaizenTitle || 'Auditoria'}</span>
                              <span className="text-[8px] opacity-60">{new Date(a.auditDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── ROADMAP TAB ── */}
              {activeTab === 'roadmap' && (
                <div className="animate-in fade-in duration-200 h-full overflow-hidden">
                  <ProjectRoadmap
                    items={roadmapItems}
                    onAddItem={handleAddRoadmapItem}
                    onUpdateItem={handleUpdateRoadmapItem}
                    onDeleteItem={handleDeleteRoadmapItem}
                  />
                </div>
              )}

              {/* ── MANAGEMENT TAB ── */}
              {activeTab === 'management' && (
                <div className="animate-in fade-in duration-200">
                  {/* Members section */}
                  <div className="overflow-hidden dark:bg-black">
                    <div className="flex items-center gap-2 border-b border-zinc-800/50 px-4 py-2 dark:border-zinc-800/50">
                      <Users size={13} className="text-slate-500" />
                      <h3 className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">Equipe do Projeto</h3>
                    </div>
                    <div>
                      <DataGridEnterprise
                        columns={membersColumns}
                        data={members as ProjectMember[]}
                        rowKey={r => r.user_id}
                        isLoading={membersLoading}
                        emptyTitle="Nenhum membro no projeto"
                        emptySubtitle="Adicione membros pelo ribbon ou convide por email"
                      />
                      {pendingInvites.length > 0 && (
                        <div className="mt-3">
                          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                            Convites Pendentes ({pendingInvites.length})
                          </h4>
                          <div className="rounded border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                            {pendingInvites.map(inv => (
                              <div key={inv.id} className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-[11px] last:border-0 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                  <Mail size={12} className="text-slate-400" />
                                  <span className="font-medium text-slate-700 dark:text-slate-300">{inv.email}</span>
                                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                    pendente
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">
                                    expira {new Date(inv.expires_at).toLocaleDateString('pt-BR')}
                                  </span>
                                  <button
                                    onClick={() => handleRevokeInvite(inv)}
                                    className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                    title="Revogar"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DMS Folders */}
                  <DmsFolders
                    folders={dmsFolders}
                    onChange={saveDms}
                    projectId={projectId}
                    attachments={attachments}
                    attachmentsLoading={attachmentsLoading}
                    onDeleteAttachment={handleDeleteAttachment}
                    onUploadClick={() => fileRef.current?.click()}
                    onLinkIndicator={(folderId, subId) => { setLinkIndicatorModal({ folderId, subId }); setLinkIndicatorSearch(''); }}
                    onKaizenReport={(sub, parentName) => setKaizenReportModal({ sub, parentName })}
                  />
                </div>
              )}

            </div>
          </div>

          {/* ── JOURNEY SIDEBAR ── */}
          {showJourney && (
            <WorkspaceModeProvider>
            <ProjectJourneySidebar
              isOpen={showJourney}
              onClose={() => setShowJourney(false)}
              currentStep={journeyStep}
              step5Reviews={audits}
              onStepClick={(step: ProjectStep) => {
                const stepNumbers: Record<ProjectStep, number> = {
                  diagnostico: 1, design: 2, planejamento: 3, execucao: 4, sustentacao: 5
                };
                setJourneyStep(stepNumbers[step] ?? 1);
                // Step 5 (Sustentação) focuses KPI tab
                if (step === 'sustentacao') setActiveTab('kpi');
              }}
            />
            </WorkspaceModeProvider>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} />

      {/* ── Create/Edit Plan modal ── */}
      <EnterpriseModal
        open={showPlanModal}
        onClose={() => { setShowPlanModal(false); resetPlanForm(); }}
        title={editingPlan ? 'Editar Plano' : 'Novo Plano'}
      >
        <div className="space-y-3">
          <ModalField label="Nome *">
            <input type="text" value={planName} onChange={e => setPlanName(e.target.value)}
              placeholder="Nome do plano" className={modalInputClass} autoFocus />
          </ModalField>
          {!editingPlan && (
            <ModalField label="Tipo">
              <select value={planType} onChange={e => setPlanType(e.target.value as PlanType)} className={modalSelectClass}>
                {PLAN_TYPE_OPTIONS.map(t => <option key={t} value={t}>{PLAN_TYPE_LABELS[t]}</option>)}
              </select>
            </ModalField>
          )}
          <ModalField label="Descrição">
            <textarea value={planDesc} onChange={e => setPlanDesc(e.target.value)}
              rows={2} className={`${modalInputClass} min-h-[40px] resize-y`} />
          </ModalField>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <ModalSecondaryButton onClick={() => { setShowPlanModal(false); resetPlanForm(); }}>Cancelar</ModalSecondaryButton>
          <ModalPrimaryButton onClick={handleSavePlan} disabled={savingPlan}>
            {savingPlan ? 'Salvando...' : editingPlan ? 'Atualizar' : 'Criar'}
          </ModalPrimaryButton>
        </div>
      </EnterpriseModal>

      {/* ── Add member modal ── */}
      <EnterpriseModal
        open={showAddMember}
        onClose={() => { setShowAddMember(false); setSelectedMember(null); }}
        title="Adicionar Membro"
      >
        <div className="space-y-3">
          <ModalField label="Membro">
            <OrgUserSearch
              value={selectedMember?.user_id ?? null}
              onSelect={setSelectedMember}
              excludeIds={existingMemberIds}
            />
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Permissão">
              <select value={addMemberRole} onChange={e => setAddMemberRole(e.target.value as ProjectMemberRole)} className={modalSelectClass}>
                {(Object.keys(MEMBER_ROLE_LABELS) as ProjectMemberRole[]).map(r => (
                  <option key={r} value={r}>{MEMBER_ROLE_LABELS[r]}</option>
                ))}
              </select>
            </ModalField>
            <ModalField label="Função no projeto">
              <select value={addOperationalRole} onChange={e => setAddOperationalRole(e.target.value as OperationalRole)} className={modalSelectClass}>
                {(Object.keys(OPERATIONAL_ROLE_LABELS) as OperationalRole[]).map(r => (
                  <option key={r} value={r}>{OPERATIONAL_ROLE_LABELS[r]}</option>
                ))}
              </select>
            </ModalField>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <ModalSecondaryButton onClick={() => { setShowAddMember(false); setSelectedMember(null); }}>Cancelar</ModalSecondaryButton>
          <ModalPrimaryButton onClick={handleAddMember} disabled={addingMember || !selectedMember}>
            {addingMember ? 'Adicionando...' : 'Adicionar'}
          </ModalPrimaryButton>
        </div>
      </EnterpriseModal>

      {/* ── Invite by email modal ── */}
      <EnterpriseModal
        open={showInviteModal}
        onClose={resetInviteModal}
        title="Convidar por Email"
      >
        {inviteLink ? (
          <div className="space-y-3">
            <p className="text-[12px] text-slate-600 dark:text-slate-300">Convite criado! Compartilhe o link:</p>
            <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800">
              <Link2 size={14} className="shrink-0 text-slate-400" />
              <input
                type="text"
                readOnly
                value={inviteLink}
                className="flex-1 bg-transparent text-[11px] text-slate-700 outline-none dark:text-slate-300"
                onClick={e => (e.target as HTMLInputElement).select()}
              />
              <button onClick={handleCopyLink} className="shrink-0 rounded bg-blue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-700">
                {linkCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <ModalPrimaryButton onClick={resetInviteModal}>Fechar</ModalPrimaryButton>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <ModalField label="Email *">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="usuario@empresa.com"
                  className={modalInputClass}
                  autoFocus
                />
              </ModalField>
              <div className="grid grid-cols-2 gap-3">
                <ModalField label="Permissão no projeto">
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value as ProjectMemberRole)} className={modalSelectClass}>
                    {(Object.keys(MEMBER_ROLE_LABELS) as ProjectMemberRole[]).map(r => (
                      <option key={r} value={r}>{MEMBER_ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </ModalField>
                <ModalField label="Função no projeto">
                  <select value={inviteOpRole} onChange={e => setInviteOpRole(e.target.value as OperationalRole)} className={modalSelectClass}>
                    {(Object.keys(OPERATIONAL_ROLE_LABELS) as OperationalRole[]).map(r => (
                      <option key={r} value={r}>{OPERATIONAL_ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </ModalField>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <ModalSecondaryButton onClick={resetInviteModal}>Cancelar</ModalSecondaryButton>
              <ModalPrimaryButton onClick={handleSendInvite} disabled={sendingInvite || !inviteEmail.trim()}>
                {sendingInvite ? 'Enviando...' : 'Gerar Convite'}
              </ModalPrimaryButton>
            </div>
          </>
        )}
      </EnterpriseModal>

      {/* ── A3 One-Page Report ── */}
      {showA3Report && (
        <ProjectA3Report
          project={project}
          summary={summary}
          journeyStep={journeyStep}
          actions={projectActions}
          audits={audits}
          totalActionCost={totalActionCost}
          ganhoRealizadoCount={ganhoRealizadoCount}
          isGenerating={a3Generating}
          onClose={() => setShowA3Report(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* ── Link Indicator Modal ── */}
      {linkIndicatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setLinkIndicatorModal(null)}>
          <div className="w-full max-w-md rounded border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <LinkIcon size={14} className="text-violet-500" />
                <h3 className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">Vincular Indicador ao Kaizen</h3>
              </div>
              <button onClick={() => setLinkIndicatorModal(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
            </div>
            <div className="p-4">
              <input
                autoFocus
                type="text"
                value={linkIndicatorSearch}
                onChange={e => setLinkIndicatorSearch(e.target.value)}
                placeholder="Buscar indicador..."
                className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
              <div className="mt-2 max-h-64 overflow-y-auto space-y-1">
                {allOrgIndicators
                  .filter(i => i.name.toLowerCase().includes(linkIndicatorSearch.toLowerCase()))
                  .map(indicator => {
                    const h = getIndicatorHealth(indicator);
                    const hColor = h === 'healthy' ? 'text-emerald-600' : h === 'warning' ? 'text-amber-600' : h === 'critical' ? 'text-red-600' : 'text-slate-400';
                    return (
                      <button
                        key={indicator.id}
                        onClick={() => handleLinkIndicator(indicator)}
                        className="flex w-full items-center gap-3 rounded px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <Activity size={13} className={hColor} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-medium text-slate-700 dark:text-slate-200 truncate">{indicator.name}</p>
                          <p className="text-[10px] text-slate-400">{indicator.current_value} / meta: {indicator.target_value ?? '—'}</p>
                        </div>
                        <span className={`text-[10px] font-medium ${hColor}`}>{h}</span>
                      </button>
                    );
                  })}
                {allOrgIndicators.filter(i => i.name.toLowerCase().includes(linkIndicatorSearch.toLowerCase())).length === 0 && (
                  <p className="py-4 text-center text-[11px] text-slate-400">Nenhum indicador encontrado</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Kaizen One-Page Report Modal ── */}
      {kaizenReportModal && (
        <KaizenReportModal
          sub={kaizenReportModal.sub}
          parentName={kaizenReportModal.parentName}
          onClose={() => setKaizenReportModal(null)}
        />
      )}

      {/* ── Audit Modal ── */}
      {showAuditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAuditModal(false)}>
          <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded border border-slate-700 bg-slate-900 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-3">
              <div className="flex items-center gap-2">
                <Shield size={14} className="text-emerald-400" />
                <h3 className="text-[13px] font-semibold text-slate-100">Auditorias de Sustentação</h3>
                <span className="text-[10px] text-slate-400">{audits.length} registro{audits.length !== 1 ? 's' : ''}</span>
              </div>
              <button onClick={() => setShowAuditModal(false)} className="text-slate-400 hover:text-slate-200"><X size={14} /></button>
            </div>
            <div className="p-4">
              <SustainabilityAuditView
                audits={audits}
                criteria={DEFAULT_AUDIT_CRITERIA}
                projectId={projectId}
                onAddAudit={(partial) => { handleAddAudit(partial); setShowAuditModal(false); }}
                onUpdateAudit={handleUpdateAudit}
              />
            </div>
          </div>
        </div>
      )}

      <LinkKpiModal
        open={showLinkProjectKpiModal && Boolean(projectId)}
        projectId={projectId}
        onClose={() => setShowLinkProjectKpiModal(false)}
        onActivateKpi={handleBindProjectKpi}
        isBinding={bindingProjectKpi}
      />

      <ToastContainer />
    </ModuleLayout>
  );
};

// ─── KpiChip ──────────────────────────────────────────────────

function KpiChip({ label, value, warn }: { label: string; value: number | string; warn?: boolean }) {
  return (
    <span>
      {label}: <strong className={warn ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}>{value}</strong>
    </span>
  );
}

// ─── PlanCard ─────────────────────────────────────────────────

function PlanCard({
  plan, metrics, onOpen, onEdit, onDelete,
}: {
  plan: ProjectPlan;
  metrics?: PlanMetrics;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const progress = metrics ? ProjectPlanService.computePlanProgress(plan.type, metrics) : 0;
  const hasData = metrics
    ? (metrics.actions_total ?? 0) + (metrics.issues_total ?? 0) + (metrics.executions_total ?? 0) > 0
    : false;

  // Alert status per type
  const alertStatus: 'critical' | 'alert' | 'ok' = useMemo(() => {
    if (!metrics) return 'ok';
    if (plan.type === 'action' && (metrics.actions_overdue ?? 0) > 0) return 'alert';
    if (plan.type === 'process' && (metrics.executions_sla_risk ?? 0) > 0) return 'critical';
    if (plan.type === 'improvement' && (metrics.issues_critical ?? 0) > 0) return 'critical';
    return 'ok';
  }, [plan.type, metrics]);

  const borderColor = alertStatus === 'critical'
    ? 'border-red-300 dark:border-red-800'
    : alertStatus === 'alert'
    ? 'border-amber-300 dark:border-amber-800'
    : 'border-slate-200 dark:border-slate-800';

  // Smart CTA based on alert
  const primaryCta = useMemo(() => {
    if (alertStatus === 'critical' || alertStatus === 'alert') {
      const labels: Record<PlanType, string> = {
        action: 'Corrigir agora',
        process: 'Abrir execuções',
        improvement: 'Atacar problema',
      };
      return { label: labels[plan.type], urgent: true };
    }
    const labels: Record<PlanType, string> = {
      action: '▶ Executar ações',
      process: '▶ Iniciar execução',
      improvement: '▶ Resolver problema',
    };
    return { label: labels[plan.type], urgent: false };
  }, [alertStatus, plan.type]);

  return (
    <div className={`flex flex-col rounded-md border bg-white p-3 dark:bg-slate-900/50 ${borderColor}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {alertStatus !== 'ok'
            ? <AlertTriangle size={14} className={alertStatus === 'critical' ? 'text-red-500' : 'text-amber-500'} />
            : PLAN_ICON[plan.type]
          }
          <h3 className="text-[12px] font-semibold text-slate-800 dark:text-slate-200 truncate">{plan.name}</h3>
        </div>
        <EnterpriseBadge className={alertStatus === 'critical'
          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          : alertStatus === 'alert'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          : PLAN_TYPE_COLORS[plan.type]
        }>
          {alertStatus === 'critical' ? 'Crítico' : alertStatus === 'alert' ? 'Atenção' : PLAN_TYPE_LABELS[plan.type]}
        </EnterpriseBadge>
      </div>

      <div className="mt-1 min-h-[28px]">
        {plan.description ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{plan.description}</p>
        ) : (
          <p className="text-[11px] text-slate-400 dark:text-slate-600 italic">Sem descrição</p>
        )}
      </div>

      {/* Progress — only when there's real data */}
      {hasData ? (
        <div className="mt-2">
          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">
            <span>Progresso</span>
            <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className={`h-1.5 rounded-full transition-all ${progress >= 100 ? 'bg-emerald-500' : progress > 0 ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-700'}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      ) : (
        <p className="mt-2 text-[10px] italic text-slate-400 dark:text-slate-500">Sem execução iniciada</p>
      )}

      {/* Metrics by type */}
      {hasData && (
        <div className="mt-2 space-y-0.5 text-[10px] text-slate-500 dark:text-slate-400">
          {plan.type === 'action' && (
            <>
              <MetricRow label="Ações" value={metrics?.actions_total ?? 0} />
              <MetricRow label="Concluídas" value={metrics?.actions_completed ?? 0} good />
              <MetricRow label="Atrasadas" value={metrics?.actions_overdue ?? 0} danger />
            </>
          )}
          {plan.type === 'process' && (
            <>
              <MetricRow label="Execuções ativas" value={metrics?.executions_active ?? 0} />
              <MetricRow label="Concluídas" value={metrics?.executions_completed ?? 0} good />
              <MetricRow label="SLA em risco" value={metrics?.executions_sla_risk ?? 0} danger />
            </>
          )}
          {plan.type === 'improvement' && (
            <>
              <MetricRow label="Problemas" value={metrics?.issues_total ?? 0} />
              <MetricRow label="Resolvidos" value={metrics?.issues_resolved ?? 0} good />
              <MetricRow label="Críticos" value={metrics?.issues_critical ?? 0} danger />
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto pt-3 flex flex-col gap-1.5">
        <button
          onClick={onOpen}
          className={`w-full rounded px-2 py-1.5 text-[10px] font-medium ${
            primaryCta.urgent
              ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30'
          }`}
        >
          {primaryCta.urgent && <Zap size={10} className="inline mr-1 -mt-px" />}
          {primaryCta.label}
        </button>
        <div className="flex items-center justify-between">
          <button
            onClick={onOpen}
            className="text-[10px] text-blue-600 hover:underline dark:text-blue-400"
          >
            Ver detalhes
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="rounded px-2 py-1 text-[10px] text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Editar
            </button>
            <button
              onClick={onDelete}
              className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MetricRow ────────────────────────────────────────────────

function MetricRow({ label, value, danger, good }: { label: string; value: number; danger?: boolean; good?: boolean }) {
  const color = danger && value > 0
    ? 'text-red-600 dark:text-red-400'
    : good && value > 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-slate-700 dark:text-slate-300';
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={`font-mono font-medium ${color}`}>{value}</span>
    </div>
  );
}

// ─── FilesTab ─────────────────────────────────────────────────

function FilesTab({
  attachments, isLoading, onDelete,
}: {
  attachments: ProjectAttachment[];
  isLoading: boolean;
  onDelete: (a: ProjectAttachment) => void;
}) {
  if (isLoading) {
    return <div className="py-12 text-center text-[12px] text-slate-400">Carregando arquivos...</div>;
  }
  if (!attachments.length) {
    return (
      <div className="flex flex-col items-center py-16 text-[12px] text-slate-400">
        <Paperclip size={28} className="mb-2 text-slate-300 dark:text-slate-600" />
        <p>Nenhum arquivo anexado</p>
        <p className="text-[11px] mt-0.5">Use o botão "Anexar Arquivo" no ribbon</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {attachments.map(a => (
        <div key={a.id} className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2.5 dark:border-slate-800 dark:bg-slate-900/50">
          <FileText size={16} className="shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-slate-700 dark:text-slate-200">{a.name}</p>
            <p className="text-[10px] text-slate-400">{a.file_size ? bytesLabel(a.file_size) : ''} · {fmtDate(a.created_at)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a href={a.file_url} target="_blank" rel="noreferrer" className="rounded p-1 text-slate-400 hover:text-blue-500" title="Baixar">
              <Download size={13} />
            </a>
            <a href={a.file_url} target="_blank" rel="noreferrer" className="rounded p-1 text-slate-400 hover:text-blue-500" title="Abrir">
              <ExternalLink size={13} />
            </a>
            <button onClick={() => onDelete(a)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20" title="Excluir">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── DmsFolders ────────────────────────────────────────────────
function getFileIcon(type: DmsFile['type']) {
  switch (type) {
    case 'pdf': return <FileText size={13} className="text-red-500" />;
    case 'excel': return <FileText size={13} className="text-emerald-500" />;
    case 'image': return <Image size={13} className="text-blue-500" />;
    case 'word': return <BookOpen size={13} className="text-blue-400" />;
    default: return <File size={13} className="text-slate-400" />;
  }
}

function detectFileType(name: string): DmsFile['type'] {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'word';
  return 'other';
}

function DmsFolderRow({
  folder, onToggle, onRename, onDelete, onDrop, onAddFile, dragOverId, setDragOverId,
  onAddSubfolder, onDeleteSubfolder, onLinkIndicator, onKaizenReport,
}: {
  folder: DmsFolder;
  onToggle: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onDrop: (files: FileList, folderId: string) => void;
  onAddFile: (folderId: string) => void;
  dragOverId: string | null;
  setDragOverId: (id: string | null) => void;
  onAddSubfolder?: (parentId: string, name: string) => void;
  onDeleteSubfolder?: (parentId: string, subId: string) => void;
  onLinkIndicator?: (parentId: string, subId: string) => void;
  onKaizenReport?: (sub: DmsFolder, parentName: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(folder.name);
  const [addingSub, setAddingSub] = useState(false);
  const [subName, setSubName] = useState('');

  const isDragOver = dragOverId === folder.id;
  const isEmpty = folder.files.length === 0 && (!folder.subfolders || folder.subfolders.length === 0);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOverId(folder.id); }}
      onDragLeave={() => setDragOverId(null)}
      onDrop={e => { e.preventDefault(); setDragOverId(null); if (e.dataTransfer.files.length) onDrop(e.dataTransfer.files, folder.id); }}
      className={`transition-colors ${isDragOver ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
    >
      {/* Folder header */}
      <div className="flex items-center gap-2 px-4 py-2">
        <button onClick={onToggle} className="flex flex-1 items-center gap-2 text-left min-w-0">
          <ChevronRight size={12} className={`shrink-0 text-slate-400 transition-transform ${folder.isExpanded ? 'rotate-90' : ''}`} />
          <Folder size={14} className="shrink-0 text-amber-400" />
          {renaming ? (
            <input
              autoFocus
              value={renameVal}
              onChange={e => setRenameVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { onRename(renameVal); setRenaming(false); } if (e.key === 'Escape') setRenaming(false); }}
              onClick={e => e.stopPropagation()}
              className="flex-1 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[11px] text-slate-700 outline-none dark:border-blue-700 dark:bg-blue-900/30 dark:text-slate-200"
            />
          ) : (
            <span className="text-[12px] font-medium text-slate-700 dark:text-slate-200 truncate">{folder.name}</span>
          )}
          <span className="shrink-0 text-[10px] text-slate-400">
            ({folder.files.length}{folder.subfolders?.length ? ` · ${folder.subfolders.length} kaizens` : ''})
            {isEmpty && <span className="ml-1 italic text-slate-300 dark:text-slate-600">vazia</span>}
          </span>
        </button>
        {folder.allowSubfolders && (
          <button
            onClick={() => setAddingSub(v => !v)}
            className="rounded p-1 text-slate-400 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/20"
            title="Novo Kaizen"
          >
            <FolderPlus size={11} />
          </button>
        )}
        <button
          onClick={() => { setRenaming(true); setRenameVal(folder.name); }}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
          title="Renomear"
        >
          <FileText size={11} />
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
          title="Excluir pasta"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* Add subfolder input (Kaizens only) */}
      {folder.allowSubfolders && addingSub && (
        <div className="mx-4 mb-1 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 dark:border-amber-800 dark:bg-amber-900/20">
          <Folder size={12} className="shrink-0 text-amber-500" />
          <input
            autoFocus
            value={subName}
            onChange={e => setSubName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && subName.trim()) {
                onAddSubfolder?.(folder.id, subName.trim());
                setSubName('');
                setAddingSub(false);
              }
              if (e.key === 'Escape') setAddingSub(false);
            }}
            placeholder="Ex: Kaizen SMED - Set/26"
            className="flex-1 bg-transparent text-[11px] text-slate-700 outline-none dark:text-slate-200"
          />
          <button onClick={() => {
            if (subName.trim()) { onAddSubfolder?.(folder.id, subName.trim()); setSubName(''); setAddingSub(false); }
          }} className="text-[10px] font-medium text-amber-700 hover:text-amber-900">Criar</button>
          <button onClick={() => setAddingSub(false)} className="text-slate-400"><X size={11} /></button>
        </div>
      )}

      {/* Folder content */}
      {folder.isExpanded && (
        <div className="px-4 pb-2">
          {/* Subfolders (Kaizens) */}
          {folder.subfolders && folder.subfolders.length > 0 && (
            <div className="mb-2 space-y-1">
              {folder.subfolders.map(sub => {
                const li = sub.linkedIndicator;
                const healthColor = !li ? '' :
                  li.health === 'healthy' ? 'border-l-2 border-emerald-400' :
                  li.health === 'warning' ? 'border-l-2 border-amber-400' :
                  li.health === 'critical' ? 'border-l-2 border-red-500 animate-pulse' : '';
                const liIcon = !li ? null :
                  li.health === 'healthy' ? <CheckCircle2 size={11} className="text-emerald-500" /> :
                  li.health === 'warning' ? <MinusCircle size={11} className="text-amber-500" /> :
                  li.health === 'critical' ? <XCircle size={11} className="text-red-500" /> :
                  <Activity size={11} className="text-slate-400" />;
                return (
                  <div key={sub.id} className={`flex items-center gap-2 rounded px-2 py-1.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/50 dark:hover:bg-slate-800 ${healthColor}`}>
                    <Folder size={12} className="shrink-0 text-amber-300" />
                    <span className="flex-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate">{sub.name}</span>
                    {li && (
                      <div className="flex items-center gap-1 shrink-0">
                        {liIcon}
                        <span className="text-[9px] text-slate-500 dark:text-slate-400 max-w-[80px] truncate">{li.indicatorName}</span>
                        <span className="text-[9px] font-mono font-bold text-slate-700 dark:text-slate-200">{li.currentValue}</span>
                      </div>
                    )}
                    <span className="text-[9px] text-slate-400">{sub.files.length} arq.</span>
                    <button
                      onClick={() => onLinkIndicator?.(folder.id, sub.id)}
                      className="rounded p-0.5 text-slate-300 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-900/20"
                      title="Vincular Indicador"
                    >
                      <LinkIcon size={10} />
                    </button>
                    <button
                      onClick={() => onKaizenReport?.(sub, folder.name)}
                      className="rounded p-0.5 text-slate-300 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20"
                      title="Relatório One-Page"
                    >
                      <Printer size={10} />
                    </button>
                    <button onClick={() => onDeleteSubfolder?.(folder.id, sub.id)} className="text-slate-300 hover:text-red-400">
                      <Trash2 size={10} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Files */}
          {folder.files.length === 0 && (!folder.subfolders || folder.subfolders.length === 0) ? (
            <div className="flex flex-col items-center rounded border-2 border-dashed border-slate-200 py-3 text-center dark:border-slate-700">
              <FilePlus2 size={16} className="mb-1 text-slate-300 dark:text-slate-600" />
              <p className="text-[10px] text-slate-400">Arraste arquivos aqui</p>
            </div>
          ) : (
            <div className="space-y-1">
              {folder.files.map(file => (
                <div key={file.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  {getFileIcon(file.type)}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium text-slate-700 dark:text-slate-200">{file.name}</p>
                    <p className="text-[9px] text-slate-400">
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB · ` : ''}
                      {new Date(file.uploadedAt).toLocaleDateString('pt-BR')}
                      {file.author ? ` · ${file.author}` : ''}
                    </p>
                  </div>
                  {file.url && (
                    <a href={file.url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-blue-500">
                      <Download size={11} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DmsFolders({
  folders, onChange, projectId, attachments, attachmentsLoading, onDeleteAttachment, onUploadClick,
  onLinkIndicator, onKaizenReport,
}: {
  folders: DmsFolder[];
  onChange: (f: DmsFolder[]) => void;
  projectId: string;
  attachments: ProjectAttachment[];
  attachmentsLoading: boolean;
  onDeleteAttachment: (a: ProjectAttachment) => void;
  onUploadClick: () => void;
  onLinkIndicator?: (folderId: string, subId: string) => void;
  onKaizenReport?: (sub: DmsFolder, parentName: string) => void;
}) {
  const [newFolderName, setNewFolderName] = useState('');
  const [addingFolder, setAddingFolder] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  // Hide fully empty non-subfolder-enabled folders unless expanded
  // Show all folders always (keep structure but mark empty ones)

  const addFolder = () => {
    if (!newFolderName.trim()) return;
    const newF: DmsFolder = {
      id: `folder-${Date.now()}`,
      name: newFolderName.trim(),
      files: [],
      isExpanded: true,
    };
    onChange([...folders, newF]);
    setNewFolderName('');
    setAddingFolder(false);
  };

  const handleDrop = (files: FileList, folderId: string) => {
    const newFiles: DmsFile[] = Array.from(files).map(file => ({
      id: `f-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      type: detectFileType(file.name),
    }));
    onChange(folders.map(f => f.id === folderId ? { ...f, files: [...f.files, ...newFiles] } : f));
  };

  const handleRename = (folderId: string, name: string) => {
    onChange(folders.map(f => f.id === folderId ? { ...f, name } : f));
  };

  const handleDelete = (folderId: string) => {
    if (!window.confirm('Excluir pasta?')) return;
    onChange(folders.filter(f => f.id !== folderId));
  };

  const handleToggle = (folderId: string) => {
    onChange(folders.map(f => f.id === folderId ? { ...f, isExpanded: !f.isExpanded } : f));
  };

  const handleAddSubfolder = (parentId: string, name: string) => {
    const newSub: DmsFolder = {
      id: `sub-${Date.now()}`,
      name,
      files: [],
      isExpanded: false,
    };
    onChange(folders.map(f => f.id === parentId ? { ...f, subfolders: [...(f.subfolders ?? []), newSub] } : f));
  };

  const handleDeleteSubfolder = (parentId: string, subId: string) => {
    onChange(folders.map(f => f.id === parentId ? { ...f, subfolders: (f.subfolders ?? []).filter(s => s.id !== subId) } : f));
  };

  return (
    <div className="rounded border border-slate-200/60 bg-white/80 backdrop-blur-xl shadow-sm dark:border-slate-700/60 dark:bg-slate-900/40 overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <FolderOpen size={13} className="text-amber-500" />
          <h3 className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">Gestão de Documentos</h3>
          <span className="text-[10px] text-slate-400">{folders.length} pastas</span>
        </div>
        <button
          onClick={() => setAddingFolder(v => !v)}
          className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <FolderPlus size={11} /> Nova Pasta
        </button>
      </div>

      {addingFolder && (
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2 dark:border-slate-800">
          <input
            autoFocus
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addFolder(); if (e.key === 'Escape') setAddingFolder(false); }}
            placeholder="Nome da pasta..."
            className="flex-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
          <button onClick={addFolder} className="rounded bg-blue-600 px-2 py-1 text-[10px] text-white hover:bg-blue-700">Criar</button>
          <button onClick={() => setAddingFolder(false)} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
        </div>
      )}

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {folders.map(folder => (
          <DmsFolderRow
            key={folder.id}
            folder={folder}
            onToggle={() => handleToggle(folder.id)}
            onRename={name => handleRename(folder.id, name)}
            onDelete={() => handleDelete(folder.id)}
            onDrop={handleDrop}
            onAddFile={() => {}}
            dragOverId={dragOverFolderId}
            setDragOverId={setDragOverFolderId}
            onAddSubfolder={folder.allowSubfolders ? handleAddSubfolder : undefined}
            onDeleteSubfolder={folder.allowSubfolders ? handleDeleteSubfolder : undefined}
            onLinkIndicator={folder.allowSubfolders ? onLinkIndicator : undefined}
            onKaizenReport={folder.allowSubfolders ? onKaizenReport : undefined}
          />
        ))}
      </div>

      {/* Loose attachments (legacy) from Supabase */}
      {!attachmentsLoading && attachments.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Arquivos Avulsos</p>
          <div className="space-y-1">
            {attachments.map(a => (
              <div key={a.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <FileText size={13} className="shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium text-slate-700 dark:text-slate-200">{a.name}</p>
                  <p className="text-[9px] text-slate-400">{a.file_size ? `${(a.file_size / 1024).toFixed(1)} KB · ` : ''}{fmtDate(a.created_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <a href={a.file_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-500"><Download size={11} /></a>
                  <button onClick={() => onDeleteAttachment(a)} className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ProjectA3Report ──────────────────────────────────────────
// Print-optimized A3 / A4-Landscape one-page Lean report

const JOURNEY_STEP_LABELS: Record<number, string> = {
  1: 'Diagnóstico', 2: 'Oportunidades', 3: 'Planejamento', 4: 'Execução', 5: 'Sustentação',
};

function ProjectA3Report({
  project, summary, journeyStep, actions, audits,
  totalActionCost, ganhoRealizadoCount,
  isGenerating, onClose, onPrint,
}: {
  project: { name: string; description?: string | null; status: string; target_date?: string | null };
  summary: { progress: number; actions_total: number; actions_completed: number; actions_overdue: number; issues_critical: number };
  journeyStep: number;
  actions: Array<{ id: string; title: string; responsible?: string; deadline?: string; cost?: number; status: string; actionPlan?: { oQue?: string; quem?: string; quando?: string; como?: string; porque?: string; onde?: string } }>;
  audits: SustainabilityAudit[];
  totalActionCost: number;
  ganhoRealizadoCount: number;
  isGenerating: boolean;
  onClose: () => void;
  onPrint: () => void;
}) {
  const topActions = actions.filter(a => a.status !== 'done').slice(0, 10);
  const phase = JOURNEY_STEP_LABELS[journeyStep] ?? 'Diagnóstico';
  const today = new Date().toLocaleDateString('pt-BR');

  // Inject print styles once
  React.useEffect(() => {
    const id = 'a3-print-style';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @media print {
        body > * { display: none !important; }
        #a3-report { display: block !important; position: fixed; inset: 0; width: 100%; height: 100%; overflow: visible; box-shadow: none; }
        @page { size: A3 landscape; margin: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm print:bg-transparent print:inset-auto print:fixed print:top-0 print:left-0">
      {/* Screen chrome — hidden on print */}
      <div className="pointer-events-none absolute inset-0 print:hidden" />

      {/* Screen controls */}
      <div className="fixed bottom-6 right-6 z-[70] flex items-center gap-2 print:hidden">
        <button
          onClick={onClose}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-[12px] font-medium text-slate-600 shadow hover:bg-slate-50"
        >
          <X size={13} className="inline mr-1" />Fechar
        </button>
        <button
          onClick={onPrint}
          disabled={isGenerating}
          className="flex items-center gap-1.5 rounded bg-blue-600 px-4 py-2 text-[12px] font-semibold text-white shadow-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {isGenerating
            ? <><Loader2 size={13} className="animate-spin" />Compilando inteligência Lean...</>
            : <><Printer size={13} />Imprimir / Salvar PDF</>
          }
        </button>
      </div>

      {/* A3 Report Sheet */}
      <div
        id="a3-report"
        className="
          relative bg-white text-slate-800 shadow-2xl
          w-[1122px] min-h-[794px] max-h-[90vh] overflow-y-auto
          print:shadow-none print:w-full print:h-full print:max-h-none print:overflow-visible
          print:fixed print:inset-0 print:m-0 print:p-0
        "
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      >
        {/* Loading overlay */}
        {isGenerating && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 print:hidden">
            <Loader2 size={32} className="animate-spin text-slate-400 mb-3" />
            <p className="text-[13px] font-medium text-slate-500">Compilando inteligência do projeto...</p>
          </div>
        )}

        <div className="p-8 print:p-6">
          {/* ── HEADER ── */}
          <div className="mb-5 flex items-start justify-between border-b-2 border-slate-800 pb-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-6 w-1.5 rounded-full bg-slate-800" />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Relatório de Projeto · Visão Integrada</p>
              </div>
              <h1 className="text-[28px] font-black leading-tight text-slate-900">{project.name}</h1>
              {project.description && (
                <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-1">{project.description}</p>
              )}
            </div>
            <div className="shrink-0 text-right">
              <div className="flex items-center gap-2 justify-end mb-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <div key={n} className={`flex flex-col items-center gap-0.5`}>
                    <div className={`h-2 w-8 rounded-sm ${n <= journeyStep ? 'bg-slate-800' : 'bg-slate-200'}`} />
                    <span className={`text-[7px] font-semibold ${n === journeyStep ? 'text-slate-800' : 'text-slate-400'}`}>{JOURNEY_STEP_LABELS[n]?.slice(0, 6)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500">Fase: <strong className="text-slate-800">{phase}</strong></p>
              <p className="text-[10px] text-slate-400">Gerado em {today}</p>
              {project.target_date && (
                <p className="text-[10px] text-slate-400">Meta: {fmtDate(project.target_date)}</p>
              )}
            </div>
          </div>

          {/* ── KPI ROW ── */}
          <div className="mb-5 grid grid-cols-5 gap-2">
            {[
              { label: 'Progresso', value: `${summary.progress}%`, sub: `${summary.actions_completed}/${summary.actions_total} ações`, accent: 'border-l-slate-800' },
              { label: 'Ações Atrasadas', value: summary.actions_overdue, sub: 'requerem atenção', accent: summary.actions_overdue > 0 ? 'border-l-red-500' : 'border-l-emerald-500' },
              { label: 'Custo Total', value: `R$ ${totalActionCost.toLocaleString('pt-BR')}`, sub: '5W2H declarado', accent: 'border-l-slate-500' },
              { label: 'Ganho Realizado', value: `${ganhoRealizadoCount} melhorias`, sub: 'score ≥ 3 nas verificações', accent: 'border-l-emerald-600' },
              { label: 'Problemas Críticos', value: summary.issues_critical, sub: 'abertos', accent: summary.issues_critical > 0 ? 'border-l-red-500' : 'border-l-emerald-500' },
            ].map(k => (
              <div key={k.label} className={`rounded border border-slate-100 border-l-4 ${k.accent} px-3 py-2`}>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{k.label}</p>
                <p className="text-[18px] font-black leading-tight text-slate-800">{k.value}</p>
                <p className="text-[9px] text-slate-400">{k.sub}</p>
              </div>
            ))}
          </div>

          {/* ── MAIN GRID: 3 sections ── */}
          <div className="grid grid-cols-12 gap-4">

            {/* ── SEÇÃO 1: VSM (col 1–5) ── */}
            <div className="col-span-5">
              <A3SectionHeader num="1" title="O Fluxo" subtitle="Visão do Processo — Estado Atual" />
              <div className="rounded border border-slate-200 bg-slate-50 h-[180px] flex flex-col items-center justify-center gap-1 text-slate-400">
                <MapIcon size={24} className="text-slate-300" />
                <p className="text-[10px] font-medium text-slate-400">Mapa de Fluxo do Projeto</p>
                <p className="text-[9px] text-slate-300">Abra o Mapa para visualização interativa</p>
              </div>
            </div>

            {/* ── SEÇÃO 3: ROI / Performance (col 6–12) ── */}
            <div className="col-span-7">
              <A3SectionHeader num="3" title="O Resultado" subtitle="Performance & ROI" />
              <div className="grid grid-cols-3 gap-2">
                {/* Trend mini */}
                <div className="col-span-2 rounded border border-slate-200 bg-slate-50 p-2.5">
                  <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Evolução dos Resultados</p>
                  {audits.length === 0 ? (
                    <p className="text-[10px] text-slate-300 italic py-8 text-center">Sem registros de auditoria</p>
                  ) : (
                    <div className="flex items-end gap-1 h-[60px]">
                      {[...audits].slice(-12).map((a, i) => {
                        const score = a.averageScore ?? 0;
                        const pct = Math.max(8, (score / 5) * 100);
                        const color = score >= 4 ? '#10b981' : score >= 3 ? '#3b82f6' : score >= 2 ? '#f59e0b' : '#f43f5e';
                        return (
                          <div key={a.id} className="flex flex-col items-center flex-1 min-w-0" title={`${a.kaizenTitle}: ${score.toFixed(1)}`}>
                            <span className="text-[7px] text-slate-400 mb-0.5">{score.toFixed(1)}</span>
                            <div className="w-full rounded-t" style={{ height: `${pct}%`, background: color }} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Pareto mini */}
                <div className="rounded border border-slate-200 bg-slate-50 p-2.5">
                  <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Principais Perdas</p>
                  <p className="text-[9px] text-slate-300 italic py-8 text-center">Ver Dashboard</p>
                </div>
              </div>
            </div>

            {/* ── SEÇÃO 2: Ações (full width) ── */}
            <div className="col-span-12">
              <A3SectionHeader num="2" title="A Execução" subtitle={`Top ${topActions.length} Ações em Aberto (5W2H)`} />
              {topActions.length === 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 py-6 text-center text-[11px] text-slate-400">
                  Nenhuma ação em aberto
                </div>
              ) : (
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-2 py-1.5 text-left font-semibold w-[32%]">O Quê</th>
                      <th className="px-2 py-1.5 text-left font-semibold w-[14%]">Quem</th>
                      <th className="px-2 py-1.5 text-left font-semibold w-[10%]">Quando</th>
                      <th className="px-2 py-1.5 text-left font-semibold w-[10%]">Custo</th>
                      <th className="px-2 py-1.5 text-left font-semibold w-[14%]">Como</th>
                      <th className="px-2 py-1.5 text-left font-semibold w-[10%]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topActions.map((a, i) => (
                      <tr key={a.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-2 py-1.5 border-b border-slate-100 font-medium text-slate-700 truncate max-w-0">
                          {a.actionPlan?.oQue ?? a.title}
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-100 text-slate-500 truncate">
                          {a.actionPlan?.quem ?? a.responsible ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-100 text-slate-500 whitespace-nowrap">
                          {a.actionPlan?.quando ? fmtDate(a.actionPlan.quando) : a.deadline ? fmtDate(a.deadline) : '—'}
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-100 text-slate-500 font-mono">
                          {a.cost != null ? `R$ ${a.cost.toLocaleString('pt-BR')}` : '—'}
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-100 text-slate-400 truncate">
                          {a.actionPlan?.como ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-100">
                          <span className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${
                            a.status === 'doing' ? 'bg-blue-100 text-blue-700' :
                            a.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{a.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── FOOTER ── */}
          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-2 text-[8px] text-slate-400">
            <span>Workflow Manager · Relatório A3 One-Page</span>
            <span>{project.name} · Fase: {phase} · {today}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function A3SectionHeader({ num, title, subtitle }: { num: string; title: string; subtitle: string }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[10px] font-black text-white">{num}</div>
      <div>
        <span className="text-[12px] font-bold text-slate-800">{title}</span>
        <span className="ml-1.5 text-[10px] text-slate-400">{subtitle}</span>
      </div>
    </div>
  );
}

// ─── KaizenReportModal ────────────────────────────────────────
function KaizenReportModal({ sub, parentName, onClose }: {
  sub: DmsFolder;
  parentName: string;
  onClose: () => void;
}) {
  const healthColor = !sub.linkedIndicator ? 'text-slate-500' :
    sub.linkedIndicator.health === 'healthy' ? 'text-emerald-600' :
    sub.linkedIndicator.health === 'warning' ? 'text-amber-600' :
    'text-red-600';

  const healthLabel = !sub.linkedIndicator ? '—' :
    sub.linkedIndicator.health === 'healthy' ? '✅ No Target' :
    sub.linkedIndicator.health === 'warning' ? '⚠️ Atenção' :
    sub.linkedIndicator.health === 'critical' ? '🔴 Crítico' : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm print:bg-white" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl rounded border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 max-h-[90vh] overflow-y-auto print:shadow-none print:border-none print:rounded-none print:max-h-none print:max-w-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800 print:border-slate-300">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 print:text-slate-500">Relatório One-Page Kaizen</p>
            <h2 className="text-[18px] font-bold text-slate-800 dark:text-slate-100 print:text-slate-900">{sub.name}</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{parentName}</p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-blue-700"
            >
              <Printer size={12} />Imprimir
            </button>
            <button onClick={onClose} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={14} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5 print:p-4 print:space-y-3">
          {/* Metadata grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded bg-slate-50 p-3 dark:bg-slate-800/50 print:bg-slate-50 print:border print:border-slate-200">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Responsável</p>
              <p className="text-[12px] font-medium text-slate-700 dark:text-slate-200 print:text-slate-800">{sub.responsible ?? '—'}</p>
            </div>
            <div className="rounded bg-slate-50 p-3 dark:bg-slate-800/50 print:bg-slate-50 print:border print:border-slate-200">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Arquivos</p>
              <p className="text-[12px] font-medium text-slate-700 dark:text-slate-200 print:text-slate-800">{sub.files.length} documento{sub.files.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="rounded bg-slate-50 p-3 dark:bg-slate-800/50 print:bg-slate-50 print:border print:border-slate-200">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Status Andon</p>
              <p className={`text-[12px] font-bold ${healthColor} print:text-slate-800`}>{healthLabel}</p>
            </div>
          </div>

          {/* Linked Indicator */}
          {sub.linkedIndicator && (
            <div className="rounded border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-900/20 print:border-slate-300 print:bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={13} className="text-violet-500" />
                <h4 className="text-[11px] font-semibold text-violet-700 dark:text-violet-300 print:text-slate-700">Indicador Vinculado</h4>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 print:text-slate-900">{sub.linkedIndicator.indicatorName}</p>
                <div className="text-right">
                  <p className={`text-[20px] font-bold ${healthColor} print:text-slate-900`}>{sub.linkedIndicator.currentValue}</p>
                  {sub.linkedIndicator.targetValue != null && (
                    <p className="text-[10px] text-slate-500">meta: {sub.linkedIndicator.targetValue}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Documents list */}
          {sub.files.length > 0 && (
            <div>
              <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Documentos Evidência</h4>
              <div className="space-y-1">
                {sub.files.map(f => (
                  <div key={f.id} className="flex items-center gap-2 rounded border border-slate-100 px-3 py-2 dark:border-slate-800 print:border-slate-200">
                    {getFileIcon(f.type)}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-slate-700 dark:text-slate-200 print:text-slate-800 truncate">{f.name}</p>
                      <p className="text-[9px] text-slate-400">{new Date(f.uploadedAt).toLocaleDateString('pt-BR')}{f.author ? ` · ${f.author}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit status */}
          <div className="rounded border border-dashed border-slate-200 p-4 text-center dark:border-slate-700 print:border-slate-300">
            <Shield size={18} className="mx-auto mb-1 text-slate-300 dark:text-slate-600 print:text-slate-400" />
            <p className="text-[11px] text-slate-400 print:text-slate-500">
              {sub.needsAudit
                ? '⚠️ Auditoria de campo necessária para validar sustentação'
                : 'Auditoria de sustentação pendente de agendamento'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectHubPage;
